import { useCallback, useRef, useState } from 'react';
import type { CommunicationInputData } from '../../../../../hooks/Marketing';
import type { ChannelType, DefinitionFormData } from '../CampaignDefinitionStep';
import type { TargetingFormData } from '../CampaignTargetingStep';
import { OfferFormData, emptyOffer } from '../offers/offerForm';
import { couponIndicesIn, isRichSms, readSmsMessage } from '../creationPayload';
import { CHANNEL_ID } from '../channels';
import { creationContentKeys } from './campaignPayload';
import { definitionFromComm, offersFromComm, targetingFromComm } from './initialState';

type CreationPayload = Record<string, unknown>;

const terminateBlockedReason = (channel: ChannelType, noCreation: boolean, batAcknowledged: boolean, cgvAcknowledged: boolean): string | null => {
  const needsBat = channel !== 'courrier' && channel !== 'wallet' && !noCreation;
  const needsCgv = channel !== 'courrier';
  if ((!needsBat || batAcknowledged) && (!needsCgv || cgvAcknowledged)) return null;
  return needsBat
    ? 'Veuillez cocher "J\'ai pris connaissance du BAT" et des CGV avant de terminer'
    : 'Veuillez cocher les CGV avant de terminer';
};

export const useCampaignForm = (editingComm: CommunicationInputData | null | undefined) => {
  const [initialComm] = useState(editingComm ?? null);
  const [definition, setDefinition] = useState(() => definitionFromComm(initialComm));
  const [targeting, setTargeting] = useState(() => targetingFromComm(initialComm));
  const [offers, setOffers] = useState(() => offersFromComm(initialComm));
  const [usedOfferIndices, setUsedOfferIndices] = useState<Set<number>>(() => new Set());
  const [smsEnrichi, setSmsEnrichi] = useState(
    () => initialComm?.defChannelId === CHANNEL_ID.sms && isRichSms(initialComm.creationPayload),
  );
  const [isDirty, setIsDirty] = useState(false);
  const [pendingChannel, setPendingChannel] = useState<ChannelType | null>(null);
  const [hasCreation, setHasCreation] = useState(false);
  const [creationResetKey, setCreationResetKey] = useState(0);
  const [batAcknowledged, setBatAcknowledged] = useState(false);
  const [cgvAcknowledged, setCgvAcknowledged] = useState(false);
  const creationPayloadRef = useRef<CreationPayload | null>(initialComm?.creationPayload ?? null);
  const staleCreationKeysRef = useRef<Set<string>>(new Set());

  const applyChannelChange = useCallback((channel: ChannelType) => {
    setDefinition(prev => ({ ...prev, channel }));
    [creationPayloadRef.current, initialComm?.creationPayload ?? null]
      .flatMap(creationContentKeys)
      .forEach(key => staleCreationKeysRef.current.add(key));
    creationPayloadRef.current = null;
    setCreationResetKey(k => k + 1);
    setHasCreation(false);
    setBatAcknowledged(false);
    setCgvAcknowledged(false);
    // Les offres sont conservées : seul le contenu de création dépend du canal
    setUsedOfferIndices(new Set());
    setSmsEnrichi(false);
    setIsDirty(true);
  }, [initialComm]);

  const changeDefinition = useCallback((patch: Partial<DefinitionFormData>) => {
    if (patch.channel !== undefined && patch.channel !== definition.channel) {
      if (definition.channel !== '') setPendingChannel(patch.channel);
      else applyChannelChange(patch.channel);
      return;
    }
    setDefinition(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }, [definition.channel, applyChannelChange]);

  const confirmChannelChange = useCallback(() => {
    if (pendingChannel !== null) applyChannelChange(pendingChannel);
    setPendingChannel(null);
  }, [pendingChannel, applyChannelChange]);

  const changeTargeting = useCallback((patch: Partial<TargetingFormData>) => {
    setTargeting(prev => ({ ...prev, ...patch }));
    setIsDirty(true);
  }, []);

  const changeOffers = useCallback((next: OfferFormData[]) => {
    setOffers(next);
    setIsDirty(true);
  }, []);

  const changeCreationPayload = useCallback((payload: CreationPayload) => {
    const prev = creationPayloadRef.current;
    creationPayloadRef.current = prev && prev.type === payload.type ? { ...prev, ...payload } : payload;
    if (payload.type !== 'sms') return;
    setSmsEnrichi(isRichSms(payload));
    setUsedOfferIndices(couponIndicesIn(readSmsMessage(payload)));
  }, []);

  const creationOffers = {
    count: offers.length,
    titles: offers.map(o => o.title.trim()),
    add: () => setOffers(prev => [...prev, emptyOffer(String(Date.now()))]),
    markUsed: (index: number) => setUsedOfferIndices(prev => new Set(prev).add(index)),
    setUsed: setUsedOfferIndices,
    remove: (index: number) => setOffers(prev => prev.filter((_, i) => i !== index)),
  };

  return {
    initialComm,
    definition,
    changeDefinition,
    pendingChannel,
    confirmChannelChange,
    cancelChannelChange: () => setPendingChannel(null),
    targeting,
    changeTargeting,
    offers,
    changeOffers,
    usedOfferIndices,
    creationOffers,
    isRichSms: definition.channel === 'sms' && smsEnrichi,
    isDirty,
    // L'éditeur reste ouvert après un enregistrement : les modifications suivantes repartent de là
    markClean: () => setIsDirty(false),
    hasCreation,
    setHasCreation,
    creationResetKey,
    initialCreationPayload: creationResetKey > 0 ? null : initialComm?.creationPayload ?? null,
    creationPayloadRef,
    changeCreationPayload,
    staleCreationKeysRef,
    acknowledgements: {
      bat: batAcknowledged,
      cgv: cgvAcknowledged,
      onBatChange: setBatAcknowledged,
      onCgvChange: setCgvAcknowledged,
    },
    hasNoCreation: definition.channel === 'cashdesk' && definition.noCreation,
    terminateBlockedReason: terminateBlockedReason(definition.channel, definition.channel === 'cashdesk' && definition.noCreation, batAcknowledged, cgvAcknowledged),
  };
};

export type CampaignForm = ReturnType<typeof useCampaignForm>;
